import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Check, Clock3 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  DailyCheckInCompletion,
  buildDailyCheckInCompletionCopy,
  type DailyCheckInCompletionSummary,
} from 'components/daily-checkin/DailyCheckInCompletion';
import {
  DailyPulseForm,
  type DailyPulseConfirmationKind,
} from 'components/daily-checkin/DailyPulseForm';
import { DailyPulseMissedDays } from 'components/daily-checkin/DailyPulseMissedDays';
import { useToast } from 'components/ui/use-toast';
import { useAuth } from 'contexts/AuthContext';
import { DailyCheckinCustomization } from 'pages/DailyCheckinCustomization/DailyCheckinCustomization';
import type { GoalProgressConfirmation } from 'pages/Snapshot/GoalProgressModal';
import { GoalProgressModal } from 'pages/Snapshot/GoalProgressModal';
import {
  dailyPulse,
  snapshot,
  type DailyPulseAnswerValue,
  type DailyPulseOverview,
  type SnapshotDueGoal,
} from 'services/api';
import {
  isForegroundPromptBlocked,
  subscribeToForegroundPromptBlockers,
} from 'utils/foregroundPrompt';
import {
  publishDailyCheckInUpdated,
  setDailyCheckInOpen,
  subscribeToDailyCheckInRequests,
  subscribeToDailyPulseCustomizationRequests,
  subscribeToDailyPulsePreferences,
  type DailyCheckInRequest,
  type DailyPulseCustomizationRequest,
} from 'utils/dailyCheckIn';
import {
  publishSnapshotGoalUpdate,
  subscribeToSnapshotGoalCheckInRequests,
} from 'utils/snapshotGoalUpdates';

export const isEligibleSnapshotPromptPath = (pathname: string, search = '') => {
  if (pathname.startsWith('/admin')) return false;
  if (pathname.startsWith('/meetings/')) return false;
  if (pathname === '/flows' || pathname.startsWith('/flows/')) return false;
  if (/^\/courses\/[^/]+\/flows\//.test(pathname)) return false;
  // A post-call notes link takes priority while its relationship data is loading.
  if (pathname === '/mentorship') {
    const params = new URLSearchParams(search);
    if (['record', 'mentee', 'nexttime'].some((key) => params.get(key))) return false;
  }
  return !['/login', '/register', '/forgot-password', '/reset-password'].includes(pathname);
};

type QueueOutcome =
  | { kind: 'saved'; confirmation: GoalProgressConfirmation }
  | { kind: 'skipped' };

interface PulseDraft {
  targetDate: string;
  answers: Record<string, DailyPulseAnswerValue>;
  note: string;
  initialAnswers: Record<string, DailyPulseAnswerValue>;
  initialNote: string;
  initialOutcome: 'saved' | 'skipped' | null;
}

interface CompletionState extends DailyCheckInCompletionSummary {
  latestConfirmation: GoalProgressConfirmation | null;
  completedGoal: SnapshotDueGoal | null;
}

export const QUEUE_COMPLETION_DWELL_MS = 3200;
const REDUCED_MOTION_QUEUE_COMPLETION_DWELL_MS = 180;
const PULSE_SAVE_CONFIRMATION_MS = 900;
const REDUCED_MOTION_PULSE_SAVE_CONFIRMATION_MS = 300;

const emptyPulseDraft = (): PulseDraft => ({
  targetDate: '',
  answers: {},
  note: '',
  initialAnswers: {},
  initialNote: '',
  initialOutcome: null,
});

const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};

const countOutcomes = (outcomes: Record<string, QueueOutcome>) => Object.values(outcomes).reduce(
  (counts, outcome) => ({
    saved: counts.saved + (outcome.kind === 'saved' ? 1 : 0),
    skipped: counts.skipped + (outcome.kind === 'skipped' ? 1 : 0),
  }),
  { saved: 0, skipped: 0 },
);

const latestSavedConfirmation = (
  queue: SnapshotDueGoal[],
  outcomes: Record<string, QueueOutcome>,
) => {
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    const outcome = outcomes[queue[index].id];
    if (outcome?.kind === 'saved') return outcome.confirmation;
  }
  return null;
};

const latestCompletedGoal = (
  queue: SnapshotDueGoal[],
  outcomes: Record<string, QueueOutcome>,
) => [...queue].reverse().find((goal) => goal.status === 'completed' && outcomes[goal.id]?.kind === 'saved') || null;

const formatDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(year, month - 1, day));
};

const answersMatch = (
  left: Record<string, DailyPulseAnswerValue>,
  right: Record<string, DailyPulseAnswerValue>,
) => {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
};

const pulseDraftFromOverview = (overview: DailyPulseOverview): PulseDraft => {
  const enabledKeys = new Set(overview.questions.filter((question) => question.enabled).map((question) => question.key));
  // Historical answers can outlive their question's availability. The server keeps
  // those snapshots; only currently editable answers belong in this draft.
  const answers = overview.entry?.outcome === 'saved'
    ? Object.fromEntries(Object.entries(overview.entry.answers).filter(([key]) => enabledKeys.has(key)))
    : {};
  const note = overview.entry?.outcome === 'saved' ? overview.entry.note : '';
  return {
    targetDate: overview.target_date,
    answers: { ...answers },
    note,
    initialAnswers: { ...answers },
    initialNote: note,
    initialOutcome: overview.entry?.outcome || null,
  };
};

export const DailyCheckInPrompt = () => {
  const { isAuthenticated, isAuthLoading, user } = useAuth();
  const { addToast, removeToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.id ?? null;
  const eligiblePath = isEligibleSnapshotPromptPath(location.pathname, location.search);
  const foregroundPromptBlocked = useSyncExternalStore(
    subscribeToForegroundPromptBlockers,
    isForegroundPromptBlocked,
    isForegroundPromptBlocked,
  );
  const fetchedForUser = useRef<number | null>(null);
  const completionTimer = useRef<number | null>(null);
  const pulseSaveTimer = useRef<number | null>(null);
  const pulseDateRequest = useRef(0);
  const [goalQueue, setGoalQueue] = useState<SnapshotDueGoal[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [frontierIndex, setFrontierIndex] = useState(0);
  const [outcomes, setOutcomes] = useState<Record<string, QueueOutcome>>({});
  const [lastUpdate, setLastUpdate] = useState<GoalProgressConfirmation | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState('');
  const [skipping, setSkipping] = useState(false);
  const [skipError, setSkipError] = useState('');
  const [pulseOverview, setPulseOverview] = useState<DailyPulseOverview | null>(null);
  const [pulseDraft, setPulseDraft] = useState<PulseDraft>(emptyPulseDraft);
  const [pulseIncluded, setPulseIncluded] = useState(false);
  const [pulseOnly, setPulseOnly] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [pulseSaving, setPulseSaving] = useState(false);
  const [pulseSaved, setPulseSaved] = useState(false);
  const [pulseError, setPulseError] = useState('');
  const [pulseConfirmation, setPulseConfirmation] = useState<DailyPulseConfirmationKind | null>(null);
  const [pulsePreferencesChanged, setPulsePreferencesChanged] = useState(false);
  const [pulseDateChooserOpen, setPulseDateChooserOpen] = useState(false);
  const [pulseDateLoading, setPulseDateLoading] = useState('');
  const [pulseDateError, setPulseDateError] = useState('');
  const [completionState, setCompletionState] = useState<CompletionState | null>(null);
  const [customizationRequest, setCustomizationRequest] = useState<DailyPulseCustomizationRequest | null>(null);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const canPresentPrompt = eligiblePath && !foregroundPromptBlocked;
  const canPresentJourney = canPresentPrompt && !customizationOpen;
  const targetGoal = goalQueue[currentIndex] || null;
  const dailyCheckInSurfaceOpen = Boolean(
    customizationOpen
    || (canPresentJourney && (
      completionState
      || (showPulse && pulseOverview)
      || (!showPulse && !completionState && targetGoal)
    )),
  );
  const reviewingPrevious = currentIndex < frontierIndex;
  const currentOutcome = targetGoal ? outcomes[targetGoal.id] : undefined;

  useEffect(() => {
    setDailyCheckInOpen(dailyCheckInSurfaceOpen);
    return () => setDailyCheckInOpen(false);
  }, [dailyCheckInSurfaceOpen]);

  const clearTimers = useCallback(() => {
    if (completionTimer.current !== null) window.clearTimeout(completionTimer.current);
    if (pulseSaveTimer.current !== null) window.clearTimeout(pulseSaveTimer.current);
    completionTimer.current = null;
    pulseSaveTimer.current = null;
  }, []);

  const dismiss = useCallback(() => {
    pulseDateRequest.current += 1;
    clearTimers();
    setGoalQueue([]);
    setCurrentIndex(0);
    setFrontierIndex(0);
    setOutcomes({});
    setLastUpdate(null);
    setUndoing(false);
    setUndoError('');
    setSkipping(false);
    setSkipError('');
    setPulseOverview(null);
    setPulseDraft(emptyPulseDraft());
    setPulseIncluded(false);
    setPulseOnly(false);
    setShowPulse(false);
    setPulseSaving(false);
    setPulseSaved(false);
    setPulseError('');
    setPulseConfirmation(null);
    setPulsePreferencesChanged(false);
    setPulseDateChooserOpen(false);
    setPulseDateLoading('');
    setPulseDateError('');
    setCompletionState(null);
    setCustomizationOpen(false);
    setCustomizationRequest(null);
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);
  useEffect(() => () => { pulseDateRequest.current += 1; }, [userId]);

  const showCompletionToast = useCallback((completion: CompletionState) => {
    const copy = buildDailyCheckInCompletionCopy(completion);
    let toastId = '';
    const undoLast = async () => {
      if (!completion.latestConfirmation?.undo) return;
      try {
        const refreshed = await snapshot.updateGoalProgress(
          completion.latestConfirmation.goal.id,
          completion.latestConfirmation.undo,
        );
        publishSnapshotGoalUpdate(refreshed);
        publishDailyCheckInUpdated();
        removeToast(toastId);
        addToast({ title: 'Last goal update undone.', duration: 5000 });
      } catch {
        addToast({
          title: 'We couldn’t undo that update.',
          description: 'Try again from your Snapshot.',
          variant: 'destructive',
          duration: 7000,
        });
      }
    };

    toastId = addToast({
      title: (
        <span className="flex min-w-0 items-center gap-3">
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-white/15" aria-hidden="true">
            {copy.allSkipped
              ? <Clock3 className="h-3.5 w-3.5" />
              : <Check className="h-3.5 w-3.5" strokeWidth={3} />}
          </span>
          <span className="min-w-0 [overflow-wrap:anywhere]">{copy.toast}</span>
        </span>
      ),
      variant: 'snapshot',
      dismissible: false,
      duration: 8000,
      action: completion.completedGoal ? (
        <button
          type="button"
          className="min-h-11 shrink-0 px-1 text-[13px] font-bold text-white underline"
          onClick={() => navigate('/snapshot?newGoal=1')}
        >
          Set a new goal
        </button>
      ) : completion.latestConfirmation?.undo ? (
        <button
          type="button"
          className="min-h-11 shrink-0 px-1 text-[13px] font-bold text-white underline"
          onClick={() => { void undoLast(); }}
        >
          Undo last update
        </button>
      ) : undefined,
    });
  }, [addToast, navigate, removeToast]);

  const dismissCompletion = useCallback((completion: CompletionState) => {
    dismiss();
    showCompletionToast(completion);
    publishDailyCheckInUpdated();
  }, [dismiss, showCompletionToast]);

  const finishJourney = useCallback((
    nextOutcomes: Record<string, QueueOutcome>,
    pulseOutcome: CompletionState['pulseOutcome'],
    targetDate: string,
  ) => {
    const counts = countOutcomes(nextOutcomes);
    const completion: CompletionState = {
      goalsUpdated: counts.saved,
      goalsSkipped: counts.skipped,
      pulseOutcome,
      targetDate,
      pulseOnly: pulseOnly || goalQueue.length === 0,
      segmentOutcomes: [
        ...goalQueue.map((goal) => nextOutcomes[goal.id]?.kind === 'saved' ? 'saved' as const : 'skipped' as const),
        ...(pulseOutcome === 'saved' || pulseOutcome === 'skipped' ? [pulseOutcome] : []),
      ],
      latestConfirmation: latestSavedConfirmation(goalQueue, nextOutcomes),
      completedGoal: latestCompletedGoal(goalQueue, nextOutcomes),
    };
    setShowPulse(false);
    setPulseConfirmation(null);
    setPulsePreferencesChanged(false);
    setCompletionState(completion);
    if (completionTimer.current !== null) window.clearTimeout(completionTimer.current);
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    completionTimer.current = window.setTimeout(() => {
      completionTimer.current = null;
      dismissCompletion(completion);
    }, reducedMotion ? REDUCED_MOTION_QUEUE_COMPLETION_DWELL_MS : QUEUE_COMPLETION_DWELL_MS);
  }, [dismissCompletion, goalQueue, pulseOnly]);

  const loadJourney = useCallback(async (request: DailyCheckInRequest = {}) => {
    pulseDateRequest.current += 1;
    setPulseDateChooserOpen(false);
    setPulseDateLoading('');
    setPulseDateError('');
    const [goalPayload, overview] = await Promise.all([
      request.pulseOnly
        ? Promise.resolve({ due_count: 0, goals: [] as SnapshotDueGoal[] })
        : snapshot.getDueCheckIns(),
      dailyPulse.getOverview(request.targetDate),
    ]);
    const includePulse = Boolean(request.forcePulse || request.targetDate || overview.pulse_due);
    if (!goalPayload.goals.length && !includePulse) return false;

    clearTimers();
    setGoalQueue(goalPayload.goals);
    setCurrentIndex(0);
    setFrontierIndex(0);
    setOutcomes({});
    setLastUpdate(null);
    setUndoing(false);
    setUndoError('');
    setSkipping(false);
    setSkipError('');
    setPulseOverview(overview);
    setPulseDraft(pulseDraftFromOverview(overview));
    setPulseIncluded(includePulse);
    setPulseOnly(Boolean(request.pulseOnly || request.targetDate));
    setShowPulse(goalPayload.goals.length === 0 && includePulse);
    setPulseSaving(false);
    setPulseSaved(false);
    setPulseError('');
    setPulseConfirmation(null);
    setCompletionState(null);
    return true;
  }, [clearTimers]);

  useEffect(() => {
    if (!isAuthenticated || userId === null) {
      fetchedForUser.current = null;
      dismiss();
      return;
    }
    if (
      isAuthLoading
      || fetchedForUser.current === userId
      || !eligiblePath
      || foregroundPromptBlocked
    ) return;

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (cancelled || isForegroundPromptBlocked()) return;
      fetchedForUser.current = userId;
      loadJourney().catch(() => {
        // Automatic eligibility must never interrupt the surrounding page.
      });
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [dismiss, eligiblePath, foregroundPromptBlocked, isAuthenticated, isAuthLoading, loadJourney, userId]);

  useEffect(() => subscribeToDailyCheckInRequests((request) => {
    if (!isAuthenticated || isAuthLoading || userId === null || !eligiblePath || foregroundPromptBlocked) return;
    void loadJourney(request).catch(() => {
      addToast({
        title: 'We couldn’t open Daily Check-in.',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 6000,
      });
    });
  }), [addToast, eligiblePath, foregroundPromptBlocked, isAuthenticated, isAuthLoading, loadJourney, userId]);

  useEffect(() => subscribeToDailyPulseCustomizationRequests((request) => {
    if (!isAuthenticated || isAuthLoading || userId === null) return;
    setCustomizationRequest(request);
    setCustomizationOpen(true);
  }), [isAuthenticated, isAuthLoading, userId]);

  useEffect(() => subscribeToSnapshotGoalCheckInRequests(() => {
    if (!isAuthenticated || isAuthLoading || userId === null || !eligiblePath || foregroundPromptBlocked) return;
    if (goalQueue.length || showPulse) return;
    void loadJourney().catch(() => {
      // Snapshot retains its own surrounding error handling.
    });
  }), [eligiblePath, foregroundPromptBlocked, goalQueue.length, isAuthenticated, isAuthLoading, loadJourney, showPulse, userId]);

  useEffect(() => subscribeToDailyPulsePreferences((enabledKeys) => {
    const enabled = new Set(enabledKeys);
    setPulseDraft((current) => ({
      ...current,
      answers: Object.fromEntries(Object.entries(current.answers).filter(([key]) => enabled.has(key))),
      initialAnswers: Object.fromEntries(Object.entries(current.initialAnswers).filter(([key]) => enabled.has(key))),
    }));
    setPulseOverview((current) => current ? {
      ...current,
      enabled_question_keys: enabledKeys,
      has_enabled_questions: enabledKeys.length > 0,
      questions: current.questions.map((question) => ({ ...question, enabled: enabled.has(question.key) })),
    } : current);
    setPulsePreferencesChanged(true);
  }), []);

  useEffect(() => {
    if (
      !canPresentPrompt
      || !showPulse
      || !pulseIncluded
      || !pulseOverview
      || !pulsePreferencesChanged
      || pulseOverview.has_enabled_questions
    ) return;

    if (goalQueue.length === 0) {
      dismiss();
      publishDailyCheckInUpdated();
      return;
    }
    finishJourney(outcomes, 'disabled', pulseDraft.targetDate || pulseOverview.today);
  }, [
    canPresentPrompt,
    dismiss,
    finishJourney,
    goalQueue.length,
    outcomes,
    pulseDraft.targetDate,
    pulseIncluded,
    pulseOverview,
    pulsePreferencesChanged,
    showPulse,
  ]);

  if (!user) return null;

  const completeUpdate = (confirmation: GoalProgressConfirmation) => {
    const nextQueue = goalQueue.map((goal) => goal.id === confirmation.goal.id ? confirmation.goal : goal);
    const nextOutcomes = {
      ...outcomes,
      [confirmation.goal.id]: { kind: 'saved', confirmation } as QueueOutcome,
    };
    setGoalQueue(nextQueue);
    setOutcomes(nextOutcomes);
    setLastUpdate(confirmation);
    setUndoError('');
    publishDailyCheckInUpdated();

    if (reviewingPrevious) {
      setCurrentIndex(frontierIndex);
      return;
    }

    const nextFrontier = frontierIndex + 1;
    if (nextFrontier < nextQueue.length) {
      setFrontierIndex(nextFrontier);
      setCurrentIndex(nextFrontier);
      return;
    }
    if (pulseIncluded) {
      setShowPulse(true);
      return;
    }
    finishJourney(
      nextOutcomes,
      pulseOverview?.has_enabled_questions ? 'omitted' : 'disabled',
      pulseOverview?.today || new Date().toISOString().slice(0, 10),
    );
  };

  const skipCurrentGoal = async () => {
    if (!targetGoal || reviewingPrevious) return;
    setSkipping(true);
    setSkipError('');
    let skippedGoal: SnapshotDueGoal;
    try {
      skippedGoal = await snapshot.skipGoalCheckIn(targetGoal.id);
    } catch {
      setSkipError('We couldn’t skip this goal right now. Please try again.');
      setSkipping(false);
      return;
    }
    const nextQueue = goalQueue.map((goal) => goal.id === skippedGoal.id ? skippedGoal : goal);
    setGoalQueue(nextQueue);
    publishSnapshotGoalUpdate(skippedGoal);
    publishDailyCheckInUpdated();
    const nextOutcomes = {
      ...outcomes,
      [targetGoal.id]: { kind: 'skipped' } as QueueOutcome,
    };
    setOutcomes(nextOutcomes);
    setLastUpdate(null);
    setUndoError('');
    setSkipping(false);

    const nextFrontier = frontierIndex + 1;
    if (nextFrontier < nextQueue.length) {
      setFrontierIndex(nextFrontier);
      setCurrentIndex(nextFrontier);
      return;
    }
    if (pulseIncluded) {
      setShowPulse(true);
      return;
    }
    finishJourney(
      nextOutcomes,
      pulseOverview?.has_enabled_questions ? 'omitted' : 'disabled',
      pulseOverview?.today || new Date().toISOString().slice(0, 10),
    );
  };

  const syncSavedGoal = (updatedGoal: SnapshotDueGoal) => {
    setGoalQueue((current) => current.map((goal) => goal.id === updatedGoal.id ? updatedGoal : goal));
    publishSnapshotGoalUpdate(updatedGoal);
    publishDailyCheckInUpdated();
  };

  const undoPrevious = async () => {
    const confirmation = reviewingPrevious && currentOutcome?.kind === 'saved'
      ? currentOutcome.confirmation
      : lastUpdate;
    if (!confirmation?.undo) return;
    setUndoing(true);
    setUndoError('');
    try {
      const refreshed = await snapshot.updateGoalProgress(confirmation.goal.id, confirmation.undo);
      const nextOutcomes: Record<string, QueueOutcome> = {
        ...outcomes,
        [refreshed.id]: { kind: 'skipped' },
      };
      setGoalQueue((current) => current.map((goal) => goal.id === refreshed.id ? refreshed : goal));
      publishSnapshotGoalUpdate(refreshed);
      publishDailyCheckInUpdated();
      setOutcomes(nextOutcomes);
      setLastUpdate({ goal: refreshed, message: 'Update undone.' });
    } catch {
      setUndoError('We couldn’t undo that update. Please try again.');
    } finally {
      setUndoing(false);
    }
  };

  const pulseQuestions = (pulseOverview?.questions || []).filter((question) => question.enabled);
  const pulseDirty = !answersMatch(pulseDraft.answers, pulseDraft.initialAnswers)
    || pulseDraft.note !== pulseDraft.initialNote;
  const pulseHasInput = Object.keys(pulseDraft.answers).length > 0 || Boolean(pulseDraft.note.trim());
  const missedPulseDays = (pulseOverview?.days || []).filter((day) => (
    day.editable
    && day.date >= (pulseOverview?.editable_from || '')
    && day.date < (pulseOverview?.today || '')
    && day.date !== pulseDraft.targetDate
    && (day.status === 'missed' || day.status === 'skipped')
  )).sort((left, right) => right.date.localeCompare(left.date));

  const cancelPulseDateChooser = () => {
    pulseDateRequest.current += 1;
    setPulseDateChooserOpen(false);
    setPulseDateLoading('');
    setPulseDateError('');
  };

  const openPulseDateChooser = () => {
    if (pulseSaving || missedPulseDays.length === 0) return;
    if (pulseDirty) {
      setPulseConfirmation('change-date');
      return;
    }
    setPulseDateError('');
    setPulseDateChooserOpen(true);
  };

  const loadPulseDate = async (targetDate: string) => {
    if (!missedPulseDays.some((day) => day.date === targetDate)) return;
    const requestId = ++pulseDateRequest.current;
    setPulseDateLoading(targetDate);
    setPulseDateError('');
    try {
      const overview = await dailyPulse.getOverview(targetDate);
      if (requestId !== pulseDateRequest.current) return;
      // Retain the original draft until its replacement is ready, so cancellation
      // or a failed lookup can always return to the user's answers.
      setPulseOverview(overview);
      setPulseDraft(pulseDraftFromOverview(overview));
      setPulseSaved(false);
      setPulseError('');
      setPulseConfirmation(null);
      setPulseDateChooserOpen(false);
      setPulseDateLoading('');
    } catch {
      if (requestId !== pulseDateRequest.current) return;
      setPulseDateLoading('');
      setPulseDateError(targetDate);
    }
  };

  const savePulse = async () => {
    if (!pulseOverview || !pulseHasInput || pulseSaving) return;
    setPulseSaving(true);
    setPulseError('');
    try {
      await dailyPulse.saveEntry(pulseDraft.targetDate, {
        idempotency_key: newIdempotencyKey(),
        answers: pulseDraft.answers,
        note: pulseDraft.note,
      });
      setPulseSaved(true);
      publishDailyCheckInUpdated();
      const reducedMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      pulseSaveTimer.current = window.setTimeout(() => {
        pulseSaveTimer.current = null;
        finishJourney(outcomes, 'saved', pulseDraft.targetDate);
      }, reducedMotion ? REDUCED_MOTION_PULSE_SAVE_CONFIRMATION_MS : PULSE_SAVE_CONFIRMATION_MS);
    } catch {
      setPulseError('save_failed');
      setPulseSaving(false);
    }
  };

  const performPulseSkip = async () => {
    if (!pulseOverview || pulseSaving) return;
    setPulseSaving(true);
    setPulseError('');
    setPulseConfirmation(null);
    try {
      await dailyPulse.skipEntry(pulseDraft.targetDate, newIdempotencyKey());
      publishDailyCheckInUpdated();
      finishJourney(outcomes, 'skipped', pulseDraft.targetDate);
    } catch {
      setPulseError('save_failed');
      setPulseSaving(false);
    }
  };

  const requestPulseSkip = () => {
    if (pulseHasInput) {
      setPulseConfirmation('skip');
      return;
    }
    void performPulseSkip();
  };

  const requestPulseClose = () => {
    if (showPulse && (pulseSaving || pulseSaved)) return;
    if (pulseDateChooserOpen) {
      cancelPulseDateChooser();
      return;
    }
    if (pulseDirty) {
      setPulseConfirmation('close');
      return;
    }
    dismiss();
  };

  const confirmPulseDiscard = () => {
    if (pulseConfirmation === 'change-date') {
      setPulseConfirmation(null);
      setPulseDateError('');
      setPulseDateChooserOpen(true);
      return;
    }
    if (pulseConfirmation === 'skip') {
      void performPulseSkip();
      return;
    }
    dismiss();
  };

  const openCustomization = () => {
    const draftAnswers = Object.entries(pulseDraft.answers).map(([key, value]) => ({
      key,
      value,
      prompt: pulseOverview?.questions.find((question) => question.key === key)?.prompt || key,
    }));
    setCustomizationRequest({ fromDailyPulse: true, draftAnswers });
    setCustomizationOpen(true);
  };

  const journeyTotal = goalQueue.length + (pulseIncluded ? 1 : 0);
  const pulseSegments = [
    ...goalQueue.map((goal) => ({
      key: goal.id,
      status: outcomes[goal.id]?.kind === 'skipped' ? 'skipped' as const : 'handled' as const,
    })),
    ...(pulseIncluded ? [{ key: 'daily-pulse', status: 'current' as const }] : []),
  ];
  const completionAction = completionState?.completedGoal
    ? {
      label: 'Set a new goal',
      run: () => {
        dismiss();
        navigate('/snapshot?newGoal=1');
      },
    }
    : completionState?.pulseOutcome === 'disabled' && completionState.goalsUpdated > 0
      ? {
        label: 'Customize questions',
        run: () => {
          dismiss();
          setCustomizationRequest({ fromDailyPulse: false });
          setCustomizationOpen(true);
        },
      }
      : null;
  const customContent = canPresentJourney && completionState ? (
    <DailyCheckInCompletion
      {...completionState}
      actionLabel={completionAction?.label}
      onAction={completionAction?.run}
    />
  ) : canPresentJourney && showPulse && pulseOverview ? (
    pulseDateChooserOpen ? (
      <DailyPulseMissedDays
        days={missedPulseDays}
        loadingDate={pulseDateLoading}
        failedDate={pulseDateError}
        onSelect={(date) => { void loadPulseDate(date); }}
        onCancel={cancelPulseDateChooser}
      />
    ) : (
    <DailyPulseForm
      questions={pulseQuestions}
      answers={pulseDraft.answers}
      note={pulseDraft.note}
      targetDate={pulseDraft.targetDate}
      today={pulseOverview.today}
      initialOutcome={pulseDraft.initialOutcome}
      journeySegments={journeyTotal > 1 ? pulseSegments : []}
      progressLabel={journeyTotal > 1
        ? `Final step of ${journeyTotal} · Daily Pulse. ${goalQueue.length} ${goalQueue.length === 1 ? 'goal' : 'goals'} handled.`
        : undefined}
      saving={pulseSaving}
      saved={pulseSaved}
      dirty={pulseDirty || pulseDraft.initialOutcome !== 'saved'}
      error={pulseError}
      confirmation={pulseConfirmation}
      noteOnly={pulseQuestions.length === 0}
      onAnswer={(key, value) => {
        setPulseDraft((current) => {
          const answers = { ...current.answers };
          if (value === undefined) delete answers[key];
          else answers[key] = value;
          return { ...current, answers };
        });
        setPulseError('');
      }}
      onNoteChange={(note) => {
        setPulseDraft((current) => ({ ...current, note }));
        setPulseError('');
      }}
      onSave={() => { void savePulse(); }}
      onSkip={requestPulseSkip}
      onClose={requestPulseClose}
      onCustomize={openCustomization}
      onFillAnother={missedPulseDays.length > 0 ? openPulseDateChooser : undefined}
      onCancelConfirmation={() => setPulseConfirmation(null)}
      onConfirmDiscard={confirmPulseDiscard}
    />
    )
  ) : null;

  return (
    <>
      <GoalProgressModal
      goal={canPresentJourney && !showPulse && !completionState ? targetGoal : null}
      customContent={customContent}
      customContentVariant={showPulse && !completionState ? 'pulse' : 'default'}
      onRequestClose={completionState ? () => dismissCompletion(completionState) : requestPulseClose}
      queueCompletion={null}
      onOpenChange={(open) => { if (!open) dismiss(); }}
      onSaved={syncSavedGoal}
      onUpdateComplete={completeUpdate}
      onEditGoal={(goal) => {
        dismiss();
        navigate(`/snapshot?editGoal=${encodeURIComponent(goal.id)}`);
      }}
      flowContext={targetGoal ? {
        position: Math.min(currentIndex + 1, Math.max(journeyTotal, 1)),
        total: Math.max(journeyTotal, 1),
        umbrellaLabel: 'Daily check-in',
        dateLabel: pulseOverview ? formatDate(pulseOverview.today) : undefined,
        progressLabel: pulseIncluded
          ? `Goal ${currentIndex + 1} of ${goalQueue.length} · then Daily Pulse`
          : undefined,
        previousUpdate: reviewingPrevious && currentOutcome?.kind === 'saved'
          ? currentOutcome.confirmation
          : reviewingPrevious
            ? null
            : lastUpdate,
        onUndoPrevious: undoPrevious,
        previousUndoing: undoing,
        previousUndoError: undoError,
        skipSaving: skipping,
        skipError,
        onSkip: reviewingPrevious ? undefined : skipCurrentGoal,
        onBack: currentIndex > 0
          ? () => { setCurrentIndex((index) => Math.max(0, index - 1)); setUndoError(''); }
          : undefined,
        onNext: reviewingPrevious
          ? () => { setCurrentIndex((index) => Math.min(frontierIndex, index + 1)); setUndoError(''); }
          : undefined,
        reviewOutcome: reviewingPrevious ? currentOutcome?.kind : undefined,
      } : undefined}
      />
      {customizationRequest ? (
        <DailyCheckinCustomization
          open={customizationOpen}
          fromDailyPulse={customizationRequest.fromDailyPulse}
          draftAnswers={customizationRequest.draftAnswers}
          onClose={() => setCustomizationOpen(false)}
          onExited={() => setCustomizationRequest(null)}
        />
      ) : null}
    </>
  );
};

// Compatibility export for existing imports while the feature is renamed throughout the app.
export const SnapshotDueGoalPrompt = DailyCheckInPrompt;
